import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { getAllUsers, getContactUsData } from "../../../../services/CommonService";
import moment from 'moment';
import { exportToExcel } from "../../../../Utils/common-Excel";


const Users = () => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState(moment().subtract(15, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
    const itemsPerPage = 5;

    useEffect(() => {
        fetchContacts();
    }, []);

    const fetchContacts = async () => {
        try {
            setLoading(true);
            const response = await getAllUsers();
            if (!response.status == 200) {
                throw new Error('Failed to fetch contacts');
            }
            const data = response.data;
            setContacts(data);
            setLoading(false);
        } catch (err) {
            setLoading(false);
            Swal.fire({
                title: "Error",
                text: err?.response?.data?.message || err?.message,
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    };

    // excel download
    const handleDownloadExcel = () => {
        const exportData = currentContacts.map((item, index) => ({
            "S.No": index + 1,
            Name: item.name,
            Email: item.email,
            Mobile: item.phone,
            'Created On': formatDate(item.created_at),
            'Updated On': formatDate(item.updated_at)
        }));

        exportToExcel(exportData, 'UserList', 'User', 'Users Report', null, null);
    };



    // Format date to a more readable format
    const formatDate = (dateString) => {
        const date = moment(dateString).format('DD-MM-YYYY');
        return date;
    };


    // Filter contacts based on search term and date range
    const filteredContacts = contacts.filter(contact => {
        const matchesSearch = (
            contact?.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
            contact?.email?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
            contact?.phone?.includes(searchTerm) ||
            contact?.description?.toLowerCase().includes(searchTerm?.toLowerCase())
        );

        const contactDate = moment(contact.created_at);
        const isInDateRange = contactDate.isSameOrAfter(moment(startDate).startOf('day')) &&
            contactDate.isSameOrBefore(moment(endDate).endOf('day'));

        return matchesSearch && isInDateRange;
    });

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentContacts = filteredContacts.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);

    // Generate page numbers
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
    }

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                Error: {error}
            </div>
        );
    }

    return (
        <div className="container-fluid mt-4">
            <div className="card shadow">
                <div className="card-header adminCardHeader text-white">
                    <h4 className="mb-0">Users</h4>
                </div>
                <div className="card-body">
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <div className="d-flex justify-content-start align-items-center">
                                <label className="me-2">Search:</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value)
                                        setCurrentPage(1);
                                    }}
                                    style={{ width: '150px' }}
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="d-flex justify-content-end align-items-center">
                                <button className="btn btn-success mb-2" onClick={handleDownloadExcel}>
                                    <i className="bi bi-file-earmark-excel-fill me-2"></i>
                                    Excel
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>S.No</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Mobile</th>
                                    <th>Created On</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentContacts.length > 0 ? (
                                    currentContacts.map((contact, i) => (
                                        <tr key={contact.id}>
                                            <td>{i + 1}</td>
                                            <td>{contact.name}</td>
                                            <td>{contact.email}</td>
                                            <td>{contact.phone}</td>
                                            <td>{formatDate(contact.created_at)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center">No user found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <nav aria-label="Contact pagination">
                            <ul className="pagination pagination-sm justify-content-end">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        Previous
                                    </button>
                                </li>

                                {pageNumbers.map(number => (
                                    <li
                                        key={number}
                                        className={`page-item ${currentPage === number ? 'active' : ''}`}
                                    >
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(number)}
                                        >
                                            {number}
                                        </button>
                                    </li>
                                ))}

                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    )}

                    <div className="text-center text-muted mt-3">
                        {filteredContacts.length > 0 ? (
                            <small>Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredContacts.length)} of {filteredContacts.length} contacts</small>
                        ) : (
                            <small>No users to display</small>
                        )}
                    </div>
                </div>
            </div>
        </div >
    );
};

export default Users;